import { StorageService } from '../../shared/logic/storage-service';
import { VidebisTest } from '../../shared/util/types';
import { GlobalLogic } from '../../shared/logic/global-logic';
import { IVergroesserungsbedarfTelemetryData } from '../../result/util/typings';
import { getBedarf } from '../../result/util/functions';

export interface IColorScheme {
    txtColor: string;
    bgColor: string;
    label: string;
}

export interface IFontFamily {
    cssEntity: string;
    name: string;
}

const blackOnGrey: IColorScheme = { txtColor: '#646464', bgColor: '#e6e6e6',  label:'schwarz auf grau' };
const blackOnWhite: IColorScheme = { txtColor: '#000000', bgColor: '#ffffff', label:'schwarz auf weiss' };
const whiteOnBlack: IColorScheme = { txtColor: '#ffffff', bgColor: '#000000', label:'weiss auf schwarz' };
const blackOnYellow: IColorScheme = { txtColor: '#000000', bgColor: '#feff00', label:'schwarz auf gelb' };
const yellowOnBlack: IColorScheme = { txtColor: '#feff00', bgColor: '#000000', label:'gelb auf schwarz' };
const blackOnGreen: IColorScheme = { txtColor: '#000000', bgColor: '#6afe00', label:'schwarz auf grün' };
const greenOnBlack: IColorScheme = { txtColor: '#6afe00', bgColor: '#000000', label:'grün auf schwarz' };

const timesNewRoman: IFontFamily = { cssEntity: '"Times New Roman", Times, serif', name: 'Times New Roman' };
const verdana: IFontFamily = { cssEntity: 'Verdana, sans-serif', name: 'Verdana' };

export const colorSchemes = [blackOnGrey, blackOnWhite, whiteOnBlack, blackOnYellow, yellowOnBlack, blackOnGreen, greenOnBlack];
export const fontFamilies = [timesNewRoman, verdana];

export class TextTestLogic {
    currentColorScheme: IColorScheme;
    currentFontFamily: IFontFamily;
    private _finished: boolean;
    private _currentFontSizeIndex: number;
    private _storageService: StorageService;
    private _globalLogic: GlobalLogic;

    static defaultColorScheme: IColorScheme = blackOnGrey;
    static defaultFontSize: number = 12;
    static defaultFontFamily: IFontFamily = timesNewRoman;

    private static _instance: TextTestLogic | null = null;

    static fontSizes: { fontSize: number, text: string }[] = [
        { fontSize: 3, text: 'Beeinträchtigte Sehleistungen können auch zu Haltungsschäden führen' },
        { fontSize: 3.8, text: 'Finden Sie die richtige Sehhilfe für Ihre Bedürfnisse' },
        { fontSize: 4.7, text: 'Busnummern, Hausnummern, Anzeigetafeln' },
        { fontSize: 6, text: 'Brille, Braille, Beratung' },
        { fontSize: 7.5, text: 'Arbeitsplatzbeleuchtung' },
        { fontSize: 9.5, text: 'Nahbrille, Lichtschutzbrille, Lupenbrille' },
        { fontSize: 12, text: 'Videbis. Besser Sehen. Mehr Lebensqualität' },
        { fontSize: 15, text: 'Holen Sie das Optimum aus Ihrer Sehleistung' },
        { fontSize: 19, text: 'Sie finden uns in der Schlosshofer Straße 6, 1210 Wien' },
        { fontSize: 24, text: 'Sie bekommen auch ganz kurzfristig einen Termin' },    // Terminvereinbarungen können auch ganz kurzfristig stattfinden'
        { fontSize: 30, text: 'Brille, Braille, Beratung' },
        { fontSize: 37, text: 'Schutzbrille' },
        { fontSize: 47, text: 'Optimum' },
        { fontSize: 59, text: 'Nahbrille' },
        { fontSize: 75, text: 'Braille' },
        { fontSize: 94, text: 'Brille' },
        { fontSize: 118, text: 'Test' }
    ];

    static getInstance(): TextTestLogic {
        if (!TextTestLogic._instance) {
            TextTestLogic._instance = new TextTestLogic();
        }
        return TextTestLogic._instance;
    }

    updateData(colorScheme: IColorScheme | null, fontFamily: IFontFamily | null, fontSize: number | null): void {
        this._storageService.set('textTest_colorScheme', JSON.stringify(colorScheme));
        this._storageService.set('textTest_fontFamily', JSON.stringify(fontFamily));
        this._storageService.set('textTest_fontSize', fontSize);
    }

    get currentFontSizeIndex(): number {
        const buf = this._storageService.get('textTest_fontSize');
        if (buf) {
            return +buf;
        }
        return this._currentFontSizeIndex;
    }

    set currentFontSizeIndex(newFontSize: number) {
        this._currentFontSizeIndex = newFontSize;
        this._storageService.set('textTest_fontSize', newFontSize);
    }

    get finished(): boolean {
        const finishedTests = this.getFinishedTestsFromCache();
        if (finishedTests) {
            return finishedTests.includes('text-test');
        }
        return this._finished;
    }

    markTestAsFinished(): void {
        this._finished = true;
        this._globalLogic.markTestAsFinished('text-test');
    }

    resetState(): void {
        this._storageService.del(['textTest_colorScheme', 'textTest_fontFamily', 'textTest_fontSize']);
        this._globalLogic.removeTestFromFinishedList('text-test');
        this.currentColorScheme = TextTestLogic.defaultColorScheme;
        this.currentFontFamily = TextTestLogic.defaultFontFamily;
        this._currentFontSizeIndex = TextTestLogic.defaultFontSize;
    }

    getTelemetryData(): IVergroesserungsbedarfTelemetryData {
        if (!this.finished) {
            return { txtSize: null, txtColor: null, bgColor: null, fontFamily: null, bedarf: null };
        }
        return {
            txtSize: TextTestLogic.fontSizes[this.currentFontSizeIndex].fontSize,
            txtColor: this.currentColorScheme.txtColor,
            bgColor: this.currentColorScheme.bgColor,
            fontFamily: this.currentFontFamily.name,
            bedarf: getBedarf()[this.currentFontSizeIndex]
        }
    }

    private getFinishedTestsFromCache(): VidebisTest[] | null {
        const buf = this._storageService.get('videbisTestsFinished');
        if (buf) {
            return JSON.parse(buf);
        }
        return null;
    }

    private constructor() {
        this.currentColorScheme = TextTestLogic.defaultColorScheme;
        this.currentFontFamily = TextTestLogic.defaultFontFamily;
        this._finished = false;
        this._currentFontSizeIndex = TextTestLogic.defaultFontSize;
        this._storageService = StorageService.getInstance();
        this._globalLogic = GlobalLogic.getInstance();
        const buf = this._storageService.get('videbisTestsFinished');
        if (buf) {
            this._finished = (JSON.parse(buf) as VidebisTest[]).includes('text-test');
        }
    }

}
